'use strict';

const libPictProvider = require('pict-provider');
const libDashboardAccess = require('headlight-dashboard-access');

/**
 * @typedef {import('headlight-dashboard-access/source/Headlight-Dashboard-Access.js').AccessRule} AccessRule
 * @typedef {import('headlight-dashboard-access/source/Headlight-Dashboard-Access.js').FilterClause} FilterClause
 */

/** How many role matches the role search lists at once. */
const ROLE_SUGGESTION_LIMIT = 8;

/** The "typed value" choice in a filter row's value picker; every other choice is a token. */
const TYPED_VALUE = '';

/**
 * DashboardAccessConfig — the #/DashboardAccess screen. Edits the customer-level `DashboardAccess` configuration
 * (NamespaceValue `Rules`): which roles see which dashboards, the one each role opens first, and the data each dashboard
 * shows them. Platform data operations resolves the same rules (PlatformDashboard/Available) for every app that shows
 * dashboards; the preview here uses the same library, so it says exactly what a person will get.
 *
 * Same shape as Where Records Open (EntityDestinationsConfig-Provider.js): ordered rules, a searchable role picker, a
 * "who sees what" preview. Gated by the `DashboardAccess-Configure` entitlement; super-users may pick any customer.
 */
class DashboardAccessConfigProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get state()
	{
		if (!this.pict.AppData.DashboardAccessConfig)
		{
			this.pict.AppData.DashboardAccessConfig =
			{
				Loaded: false, Loading: false, Saving: false, Error: '', Message: '',
				TargetCustomerID: 0, TargetCustomerName: '', Customers: [],
				Rules: {}, Dirty: false,
				Roles: [], Dashboards: [], Fields: {},
				EditingKey: '', Draft: null,
				PreviewRoles: [],
				RoleSearch: { Draft: '', Preview: '' },
			};
		}
		return this.pict.AppData.DashboardAccessConfig;
	}

	get currentUser()
	{
		return this.pict.AppData.CurrentUser || {};
	}

	callerCustomer()
	{
		return Number(this.currentUser.CustomerID) || 0;
	}

	isSuperUser()
	{
		return (Number(this.currentUser.UserRoleIndex) === 5) && (Number(this.currentUser.CustomerID) === 1);
	}

	can()
	{
		const tmpService = this.pict.fable.EffectivePermissionsService || (this.fable && this.fable.EffectivePermissionsService);
		if (!tmpService || (typeof tmpService.can !== 'function'))
		{
			return true;
		}
		return tmpService.can('DashboardAccess', 'Configure');
	}

	/**
	 * @param {string} pURL
	 * @return {Promise<any>} the body, or null on error
	 */
	_getJSON(pURL)
	{
		const tmpClient = this.restClient;
		if (!tmpClient)
		{
			return Promise.resolve(null);
		}
		return new Promise((fResolve) => tmpClient.getJSON(pURL, (pError, pBody) => fResolve(pError ? null : pBody)));
	}

	/**
	 * @param {string} pURL
	 * @param {Object} pBody
	 * @return {Promise<{ error: any, result: any }>}
	 */
	_postJSON(pURL, pBody)
	{
		const tmpClient = this.restClient;
		if (!tmpClient)
		{
			return Promise.resolve({ error: 'no client', result: null });
		}
		return new Promise((fResolve) => tmpClient.postJSON(pURL, pBody, (pError, pResult) => fResolve({ error: pError, result: pResult })));
	}

	/**
	 * @param {any} pValue
	 * @return {string}
	 */
	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	/**
	 * @param {any} pValue
	 * @return {string} a JSON literal safe inside a single-quoted onclick attribute
	 */
	_arg(pValue)
	{
		return this._esc(JSON.stringify(pValue));
	}

	/** @return {Array<[string, AccessRule]>} rules in the order they are tried */
	orderedRules()
	{
		return Object.entries(this.state.Rules).sort(([ pKeyA, pA ], [ pKeyB, pB ]) => ((pA.Order ?? 100) - (pB.Order ?? 100)) || pKeyA.localeCompare(pKeyB));
	}

	/**
	 * @param {string} pHash
	 * @return {{ Hash: string, Title: string, Kind: string, ProjectBound: boolean, SchemaURL: string|null }}
	 */
	dashboard(pHash)
	{
		return this.state.Dashboards.find((pDashboard) => pDashboard.Hash === pHash) || { Hash: pHash, Title: `${pHash} (no longer exists)`, Kind: 'Missing', ProjectBound: false, SchemaURL: null };
	}

	/**
	 * @param {string} pHash
	 * @return {string}
	 */
	roleName(pHash)
	{
		return (this.state.Roles.find((pRole) => pRole.Hash === pHash) || { Name: pHash }).Name;
	}

	// --- Load -----------------------------------------------------------------------------------------------

	async loadAsync()
	{
		const tmpState = this.state;
		tmpState.Loading = true;
		tmpState.Error = '';
		tmpState.Message = '';
		this.render();
		try
		{
			if (this.isSuperUser())
			{
				const tmpCustomers = await this._getJSON('Customers/FilteredTo/FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/2000');
				tmpState.Customers = Array.isArray(tmpCustomers) ? tmpCustomers.map((pCustomer) => ({ IDCustomer: pCustomer.IDCustomer, Name: pCustomer.Name })) : [];
			}
			if (!tmpState.TargetCustomerID)
			{
				tmpState.TargetCustomerID = this.callerCustomer();
			}
			await this._loadCustomerAsync(tmpState.TargetCustomerID);
			tmpState.Loaded = true;
		}
		catch (pError)
		{
			tmpState.Error = `Could not load dashboard access: ${(pError && pError.message) || pError}`;
		}
		finally
		{
			tmpState.Loading = false;
			this.render();
		}
	}

	/**
	 * @param {number} pIDCustomer
	 */
	async _loadCustomerAsync(pIDCustomer)
	{
		const tmpState = this.state;
		const tmpCustomer = await this._getJSON(`Customer/${Number(pIDCustomer)}`);
		tmpState.TargetCustomerName = (tmpCustomer && tmpCustomer.Name) || '';
		const tmpConfig = await this._getJSON(`Configuration/For/${libDashboardAccess.NAMESPACE}/In/Customer/${Number(pIDCustomer)}`);
		if (tmpConfig === null)
		{
			throw new Error('the saved settings could not be read');
		}
		const tmpRules = tmpConfig[libDashboardAccess.NAMESPACE_VALUE];
		tmpState.Rules = (tmpRules && typeof tmpRules === 'object') ? JSON.parse(JSON.stringify(tmpRules)) : {};
		tmpState.Dirty = false;
		tmpState.EditingKey = '';
		tmpState.Draft = null;
		tmpState.Fields = {};

		const tmpRoles = await this._getJSON(`Roles/FilteredTo/FBL~IDCustomer~INN~0,${Number(pIDCustomer)}~FSF~Name~ASC~0/0/1000`);
		const tmpRoleRows = (Array.isArray(tmpRoles) ? tmpRoles : []).filter((pRole) => pRole && pRole.GUIDRole && pRole.Name && !pRole.Deleted && pRole.Type !== 'PermissionGroup');
		const tmpNameCounts = tmpRoleRows.reduce((pCounts, pRole) => Object.assign(pCounts, { [pRole.Name]: (pCounts[pRole.Name] || 0) + 1 }), {});
		tmpState.Roles = tmpRoleRows.map((pRole) => ({ Hash: pRole.GUIDRole, Name: (tmpNameCounts[pRole.Name] > 1 && !Number(pRole.IDCustomer)) ? `${pRole.Name} (standard)` : pRole.Name, PermissionLevel: pRole.PermissionLevel || '' }));

		const tmpDashboards = await this._getJSON(`Dashboards/FilteredTo/FBV~IDCustomer~EQ~${Number(pIDCustomer)}~FSF~Ordinal~ASC~0/0/200`);
		tmpState.Dashboards = (Array.isArray(tmpDashboards) ? tmpDashboards : []).map((pRow) =>
		{
			let tmpDataSource = pRow.DataSource;
			if (!tmpDataSource || typeof tmpDataSource !== 'object')
			{
				try
				{
					tmpDataSource = JSON.parse(pRow.DataSourceJSON || '{}') || {};
				}
				catch (pError)
				{
					tmpDataSource = {};
				}
			}
			const tmpPrefix = String(tmpDataSource.URLPrefix || '/1.0/').replace(/^\/1\.0\//, '');
			return {
				Hash: pRow.Hash, Title: pRow.Name || pRow.Hash, Kind: 'Dynamic', ProjectBound: !!tmpDataSource.ProjectField,
				SchemaURL: tmpDataSource.Entity ? `${tmpPrefix}${tmpDataSource.Entity}/Schema` : null,
			};
		});
		// The legacy app_dashboards bundles (Fieldbook and the materials app show these today) can be granted too.
		const tmpLegacy = await this._getJSON(`Configuration/For/Dashboards/In/Customer/${Number(pIDCustomer)}`);
		for (const [ tmpKey, tmpEntry ] of Object.entries((tmpLegacy && typeof tmpLegacy === 'object') ? tmpLegacy : {}))
		{
			if (tmpEntry && typeof tmpEntry === 'object')
			{
				tmpState.Dashboards.push({ Hash: tmpEntry.DashboardHash || tmpKey, Title: tmpEntry.DisplayName || tmpKey, Kind: 'Legacy', ProjectBound: true, SchemaURL: null });
			}
		}
		tmpState.PreviewRoles = [];
		tmpState.RoleSearch = { Draft: '', Preview: '' };
	}

	async selectCustomer(pIDCustomer)
	{
		const tmpState = this.state;
		tmpState.TargetCustomerID = Number(pIDCustomer) || 0;
		tmpState.Message = '';
		tmpState.Error = '';
		tmpState.Loading = true;
		this.render();
		try
		{
			await this._loadCustomerAsync(tmpState.TargetCustomerID);
		}
		catch (pError)
		{
			tmpState.Error = `Could not load dashboard access: ${(pError && pError.message) || pError}`;
		}
		tmpState.Loading = false;
		this.render();
	}

	/**
	 * The columns a dashboard's data has, for the "limit the data" field picker. Fetched once per dashboard.
	 *
	 * @param {string} pHash
	 * @return {Promise<void>}
	 */
	async loadFieldsAsync(pHash)
	{
		const tmpState = this.state;
		if (tmpState.Fields[pHash])
		{
			return;
		}
		tmpState.Fields[pHash] = [];
		const tmpDashboard = this.dashboard(pHash);
		if (!tmpDashboard.SchemaURL)
		{
			return;
		}
		const tmpSchema = await this._getJSON(tmpDashboard.SchemaURL);
		const tmpProperties = (tmpSchema && (tmpSchema.properties || (tmpSchema.Schema && tmpSchema.Schema.properties))) || {};
		tmpState.Fields[pHash] = Object.keys(tmpProperties).filter((pField) => ![ 'CreateDate', 'UpdateDate', 'DeleteDate', 'Deleted', 'CreatingIDUser', 'UpdatingIDUser', 'DeletingIDUser' ].includes(pField)).sort();
		this.render();
	}

	// --- Rule list ------------------------------------------------------------------------------------------

	_markDirty()
	{
		this.state.Dirty = true;
		this.state.Message = '';
	}

	/**
	 * @param {Array<[string, AccessRule]>} pOrdered - The rules, in the order wanted.
	 */
	_renumber(pOrdered)
	{
		pOrdered.forEach(([ , pRule ], pIndex) =>
		{
			pRule.Order = (pIndex + 1) * 10;
		});
	}

	moveRule(pKey, pDelta)
	{
		const tmpOrdered = this.orderedRules();
		const tmpIndex = tmpOrdered.findIndex(([ pRuleKey ]) => pRuleKey === pKey);
		const tmpTarget = tmpIndex + Number(pDelta);
		if (tmpIndex < 0 || tmpTarget < 0 || tmpTarget >= tmpOrdered.length)
		{
			return;
		}
		const [ tmpMoved ] = tmpOrdered.splice(tmpIndex, 1);
		tmpOrdered.splice(tmpTarget, 0, tmpMoved);
		this._renumber(tmpOrdered);
		this._markDirty();
		this.render();
	}

	toggleRule(pKey)
	{
		const tmpRule = this.state.Rules[pKey];
		if (tmpRule)
		{
			tmpRule.Enabled = (tmpRule.Enabled === false);
			this._markDirty();
			this.render();
		}
	}

	removeRule(pKey)
	{
		delete this.state.Rules[pKey];
		if (this.state.EditingKey === pKey)
		{
			this.state.EditingKey = '';
			this.state.Draft = null;
		}
		this._renumber(this.orderedRules());
		this._markDirty();
		this.render();
	}

	// --- Rule editor ----------------------------------------------------------------------------------------

	editRule(pKey)
	{
		const tmpRule = this.state.Rules[pKey];
		if (!tmpRule)
		{
			return;
		}
		this.state.EditingKey = pKey;
		this.state.RoleSearch.Draft = '';
		this.state.Draft =
		{
			Label: tmpRule.Label || '',
			Roles: (tmpRule.Audience && Array.isArray(tmpRule.Audience.Roles)) ? tmpRule.Audience.Roles.slice() : [],
			Dashboards: Array.isArray(tmpRule.Dashboards) ? tmpRule.Dashboards.slice() : [],
			Landing: tmpRule.Landing || '',
			DataFilters: JSON.parse(JSON.stringify(tmpRule.DataFilters || {})),
			Extra: { Audience: Object.assign({}, tmpRule.Audience || {}) },
		};
		for (const tmpHash of Object.keys(this.state.Draft.DataFilters))
		{
			this.loadFieldsAsync(tmpHash);
		}
		this.render();
	}

	newRule()
	{
		this.state.EditingKey = '(new)';
		this.state.RoleSearch.Draft = '';
		this.state.Draft = { Label: '', Roles: [], Dashboards: [], Landing: '', DataFilters: {}, Extra: { Audience: {} } };
		this.render();
	}

	cancelEdit()
	{
		this.state.EditingKey = '';
		this.state.Draft = null;
		this.state.Error = '';
		this.render();
	}

	setDraft(pField, pValue)
	{
		if (this.state.Draft)
		{
			this.state.Draft[pField] = pValue;
		}
	}

	/**
	 * Tick or untick a dashboard in the rule being edited. Unticking drops its data limit and, if it was the
	 * "opens first" dashboard, that too.
	 *
	 * @param {string} pHash
	 */
	toggleDraftDashboard(pHash)
	{
		const tmpDraft = this.state.Draft;
		if (!tmpDraft)
		{
			return;
		}
		const tmpIndex = tmpDraft.Dashboards.indexOf(pHash);
		if (tmpIndex >= 0)
		{
			tmpDraft.Dashboards.splice(tmpIndex, 1);
			delete tmpDraft.DataFilters[pHash];
			if (tmpDraft.Landing === pHash)
			{
				tmpDraft.Landing = '';
			}
		}
		else
		{
			tmpDraft.Dashboards.push(pHash);
		}
		this.render();
	}

	/**
	 * @param {string} pHash
	 */
	setDraftLanding(pHash)
	{
		if (this.state.Draft)
		{
			this.state.Draft.Landing = (this.state.Draft.Landing === pHash) ? '' : pHash;
			this.render();
		}
	}

	/**
	 * @param {string} pHash
	 */
	addDraftFilter(pHash)
	{
		const tmpDraft = this.state.Draft;
		if (!tmpDraft)
		{
			return;
		}
		(tmpDraft.DataFilters[pHash] = tmpDraft.DataFilters[pHash] || []).push({ Field: '', Operator: 'EQ', Value: '' });
		this.loadFieldsAsync(pHash);
		this.render();
	}

	/**
	 * @param {string} pHash
	 * @param {number} pIndex
	 */
	removeDraftFilter(pHash, pIndex)
	{
		const tmpFilters = this.state.Draft && this.state.Draft.DataFilters[pHash];
		if (Array.isArray(tmpFilters))
		{
			tmpFilters.splice(Number(pIndex), 1);
			if (tmpFilters.length < 1)
			{
				delete this.state.Draft.DataFilters[pHash];
			}
			this.render();
		}
	}

	/**
	 * @param {string} pHash
	 * @param {number} pIndex
	 * @param {'Field'|'Operator'|'Value'|'ValueSource'} pPart
	 * @param {string} pValue
	 */
	setDraftFilter(pHash, pIndex, pPart, pValue)
	{
		const tmpClause = this.state.Draft && (this.state.Draft.DataFilters[pHash] || [])[Number(pIndex)];
		if (!tmpClause)
		{
			return;
		}
		if (pPart === 'ValueSource')
		{
			tmpClause.Value = (pValue === TYPED_VALUE) ? '' : `{~D:${pValue}~}`;
			this.render();
			return;
		}
		tmpClause[pPart] = pValue;
		if (pPart === 'Operator')
		{
			this.render();
		}
	}

	/**
	 * @param {Object} pDraft
	 * @return {{ Rule?: AccessRule, Error?: string }}
	 */
	buildRule(pDraft)
	{
		const tmpLabel = String(pDraft.Label || '').trim();
		if (!tmpLabel)
		{
			return { Error: 'Give the rule a name, e.g. “Lab technicians”.' };
		}
		if (!pDraft.Roles.length)
		{
			return { Error: 'Choose at least one role. A rule with no roles applies to nobody.' };
		}
		if (!pDraft.Dashboards.length)
		{
			return { Error: 'Tick at least one dashboard for these roles to see.' };
		}
		/** @type {Record<string, Array<FilterClause>>} */
		const tmpFilters = {};
		for (const [ tmpHash, tmpClauses ] of Object.entries(pDraft.DataFilters || {}))
		{
			const tmpComplete = [];
			for (const tmpClause of tmpClauses)
			{
				const tmpField = String(tmpClause.Field || '').trim();
				const tmpValue = (typeof tmpClause.Value === 'string') ? tmpClause.Value.trim() : tmpClause.Value;
				if (!tmpField && (tmpValue === '' || tmpValue === undefined))
				{
					continue;
				}
				if (!tmpField || tmpValue === '' || tmpValue === undefined)
				{
					return { Error: `Finish or remove the half-filled data limit on ${this.dashboard(tmpHash).Title}: it needs both a field and a value.` };
				}
				if (!/^[A-Za-z0-9_]+$/.test(tmpField))
				{
					return { Error: `“${tmpField}” isn’t a field name. Pick one from the list.` };
				}
				const tmpOperator = libDashboardAccess.OPERATORS[tmpClause.Operator] ? tmpClause.Operator : 'EQ';
				const tmpIsToken = (typeof tmpValue === 'string') && /^\{~D:[A-Za-z0-9_.]+~\}$/.test(tmpValue);
				tmpComplete.push({ Field: tmpField, Operator: tmpOperator, Value: (libDashboardAccess.OPERATORS[tmpOperator].List && !tmpIsToken) ? String(tmpValue).split(',').map((pItem) => pItem.trim()).filter(Boolean) : tmpValue });
			}
			if (tmpComplete.length > 0 && pDraft.Dashboards.includes(tmpHash))
			{
				tmpFilters[tmpHash] = tmpComplete;
			}
		}
		/** @type {AccessRule} */
		const tmpRule =
		{
			Label: tmpLabel,
			Audience: Object.assign({}, (pDraft.Extra && pDraft.Extra.Audience) || {}, { Roles: pDraft.Roles.slice() }),
			Dashboards: pDraft.Dashboards.slice(),
		};
		if (pDraft.Landing && pDraft.Dashboards.includes(pDraft.Landing))
		{
			tmpRule.Landing = pDraft.Landing;
		}
		if (Object.keys(tmpFilters).length > 0)
		{
			tmpRule.DataFilters = tmpFilters;
		}
		return { Rule: tmpRule };
	}

	applyDraft()
	{
		const tmpState = this.state;
		const tmpBuilt = this.buildRule(tmpState.Draft || {});
		if (tmpBuilt.Error)
		{
			tmpState.Error = tmpBuilt.Error;
			this.render();
			return;
		}
		let tmpKey = tmpState.EditingKey;
		if (tmpKey === '(new)' || !tmpState.Rules[tmpKey])
		{
			const tmpBase = tmpBuilt.Rule.Label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'rule';
			tmpKey = tmpBase;
			for (let i = 2; tmpState.Rules[tmpKey]; i++)
			{
				tmpKey = `${tmpBase}-${i}`;
			}
			tmpBuilt.Rule.Order = Number.MAX_SAFE_INTEGER;
		}
		else
		{
			tmpBuilt.Rule.Order = tmpState.Rules[tmpKey].Order;
			if (tmpState.Rules[tmpKey].Enabled === false)
			{
				tmpBuilt.Rule.Enabled = false;
			}
		}
		tmpState.Rules[tmpKey] = tmpBuilt.Rule;
		this._renumber(this.orderedRules());
		tmpState.Error = '';
		tmpState.EditingKey = '';
		tmpState.Draft = null;
		this._markDirty();
		this.render();
	}

	// --- Save -----------------------------------------------------------------------------------------------

	async save()
	{
		const tmpState = this.state;
		if (tmpState.Saving || !tmpState.Dirty)
		{
			return;
		}
		tmpState.Saving = true;
		tmpState.Error = '';
		tmpState.Message = '';
		this.render();
		try
		{
			const tmpResult = await this._postJSON(`Configuration/Put/${libDashboardAccess.NAMESPACE}/${libDashboardAccess.NAMESPACE_VALUE}/Customer/${Number(tmpState.TargetCustomerID)}`, { Config: tmpState.Rules });
			if (tmpResult.error || (tmpResult.result && tmpResult.result.Error))
			{
				throw new Error((tmpResult.error && tmpResult.error.message) || (tmpResult.result && tmpResult.result.Error) || String(tmpResult.error));
			}
			tmpState.Dirty = false;
			if (this.pict.providers.HeadlightDashboardHost)
			{
				this.pict.providers.HeadlightDashboardHost.invalidate();
			}
			tmpState.Message = libDashboardAccess.hasRules(tmpState.Rules)
				? `Saved. People at ${tmpState.TargetCustomerName || 'this customer'} see the dashboards these rules give them, in every app, the next time they open one.`
				: `Saved. With no rules turned on, everyone at ${tmpState.TargetCustomerName || 'this customer'} sees every dashboard.`;
		}
		catch (pError)
		{
			tmpState.Error = `Save failed: ${(pError && pError.message) || pError}`;
		}
		finally
		{
			tmpState.Saving = false;
			this.render();
		}
	}

	// --- Role picker (a customer can have hundreds of roles, so they are searched, not listed) --------------

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @return {Array<string>}
	 */
	_chosenRoles(pContext)
	{
		if (pContext === 'Draft')
		{
			return (this.state.Draft && Array.isArray(this.state.Draft.Roles)) ? this.state.Draft.Roles : [];
		}
		return this.state.PreviewRoles;
	}

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @param {string} pText
	 * @return {{ Matches: Array<{ Hash: string, Name: string }>, More: number }}
	 */
	findRoles(pContext, pText)
	{
		const tmpChosen = this._chosenRoles(pContext);
		const tmpNeedle = String(pText || '').trim().toLowerCase();
		const tmpAll = this.state.Roles.filter((pRole) => !tmpChosen.includes(pRole.Hash) && (!tmpNeedle || pRole.Name.toLowerCase().includes(tmpNeedle) || pRole.PermissionLevel.toLowerCase().includes(tmpNeedle)));
		return { Matches: tmpAll.slice(0, ROLE_SUGGESTION_LIMIT), More: Math.max(tmpAll.length - ROLE_SUGGESTION_LIMIT, 0) };
	}

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @param {string} pText
	 */
	searchRoles(pContext, pText)
	{
		this.state.RoleSearch[pContext] = String(pText || '');
		if (typeof document !== 'undefined' && document.getElementById(`DAC-RoleSuggest-${pContext}`))
		{
			this.pict.ContentAssignment.assignContent(`#DAC-RoleSuggest-${pContext}`, this.renderRoleSuggestionsHTML(pContext));
		}
	}

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @param {string} pHash
	 */
	addRole(pContext, pHash)
	{
		const tmpChosen = this._chosenRoles(pContext);
		if (!tmpChosen.includes(pHash))
		{
			tmpChosen.push(pHash);
		}
		this.state.RoleSearch[pContext] = '';
		this.render();
		if (typeof document !== 'undefined' && document.getElementById(`DAC-RoleSearch-${pContext}`))
		{
			document.getElementById(`DAC-RoleSearch-${pContext}`).focus();
		}
	}

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @param {string} pHash
	 */
	dropRole(pContext, pHash)
	{
		const tmpChosen = this._chosenRoles(pContext);
		const tmpIndex = tmpChosen.indexOf(pHash);
		if (tmpIndex >= 0)
		{
			tmpChosen.splice(tmpIndex, 1);
		}
		this.render();
	}

	// --- Preview --------------------------------------------------------------------------------------------

	/**
	 * What a person with the preview roles gets from the current (unsaved) rules — the same resolver the platform runs.
	 * Values that come from the person (their labs, their projects) can't be known here, so they read as phrases.
	 *
	 * @return {ReturnType<typeof libDashboardAccess.resolveAccess>}
	 */
	preview()
	{
		const tmpState = this.state;
		const tmpRoles = tmpState.Roles.filter((pRole) => tmpState.PreviewRoles.includes(pRole.Hash));
		return libDashboardAccess.resolveAccess({ Rules: tmpState.Rules, Catalog: tmpState.Dashboards, CustomerRoles: tmpRoles, User: {} });
	}

	// --- Rendering ------------------------------------------------------------------------------------------

	/**
	 * @param {Array<FilterClause>} pClauses
	 * @return {string}
	 */
	describeFilters(pClauses)
	{
		return pClauses.map((pClause) => libDashboardAccess.describeClause(pClause)).join(' and ');
	}

	/**
	 * @param {AccessRule} pRule
	 * @return {string} the rule as a sentence: who sees what, opens what first, and any data limits
	 */
	describeRule(pRule)
	{
		const tmpWho = ((pRule.Audience && pRule.Audience.Roles) || []).map((pHash) => this.roleName(pHash)).join(', ') || 'Nobody (no roles chosen)';
		const tmpWhat = (pRule.Dashboards || []).map((pHash) => this.dashboard(pHash).Title).join(', ') || 'no dashboards';
		let tmpSentence = `${this._esc(tmpWho)} see <b>${this._esc(tmpWhat)}</b>`;
		if (pRule.Landing)
		{
			tmpSentence += ` · open <b>${this._esc(this.dashboard(pRule.Landing).Title)}</b> first`;
		}
		for (const [ tmpHash, tmpClauses ] of Object.entries(pRule.DataFilters || {}))
		{
			tmpSentence += `<br>${this._esc(this.dashboard(tmpHash).Title)} shows only rows where ${this._esc(this.describeFilters(tmpClauses))}`;
		}
		return tmpSentence;
	}

	render()
	{
		const tmpContainer = (typeof document !== 'undefined') ? document.getElementById('DAC-Detail') : null;
		const tmpHTML = this.renderHTML();
		if (tmpContainer)
		{
			this.pict.ContentAssignment.assignContent('#DAC-Detail', tmpHTML);
		}
		return tmpHTML;
	}

	/** @return {string} */
	renderHTML()
	{
		const tmpState = this.state;
		if (!this.can())
		{
			return '<div class="edc-empty">You don’t have the “Configure dashboard access” entitlement.</div>';
		}
		if (tmpState.Loading)
		{
			return '<div class="edc-empty"><i class="fas fa-spinner fa-spin"></i> Loading…</div>';
		}
		const tmpCustomer = (this.isSuperUser() && tmpState.Customers.length)
			? `<select class="edc-select" onchange="_Pict.providers.DashboardAccessConfig.selectCustomer(this.value)">${tmpState.Customers.map((pCustomer) => `<option value="${Number(pCustomer.IDCustomer)}" ${Number(pCustomer.IDCustomer) === Number(tmpState.TargetCustomerID) ? 'selected' : ''}>${this._esc(pCustomer.Name)}</option>`).join('')}</select>`
			: `<span class="edc-customer">${this._esc(tmpState.TargetCustomerName)}</span>`;
		const tmpOrdered = this.orderedRules();
		const tmpRows = tmpOrdered.map(([ pKey, pRule ], pIndex) =>
		{
			const tmpKeyArg = this._arg(pKey);
			return `<div class="edc-rule ${pRule.Enabled === false ? 'is-off' : ''}">
				<div class="edc-rule-order">
					<button class="edc-mv" title="Earlier" ${pIndex === 0 ? 'disabled' : ''} onclick='_Pict.providers.DashboardAccessConfig.moveRule(${tmpKeyArg}, -1)'><i class="fas fa-chevron-up"></i></button>
					<button class="edc-mv" title="Later" ${pIndex === tmpOrdered.length - 1 ? 'disabled' : ''} onclick='_Pict.providers.DashboardAccessConfig.moveRule(${tmpKeyArg}, 1)'><i class="fas fa-chevron-down"></i></button>
				</div>
				<div class="edc-rule-body">
					<div class="edc-rule-label">${this._esc(pRule.Label || pKey)}${pRule.Enabled === false ? ' <span class="edc-off">Off</span>' : ''}</div>
					<div class="edc-rule-sentence">${this.describeRule(pRule)}</div>
				</div>
				<div class="edc-rule-actions">
					<button class="edc-btn" onclick='_Pict.providers.DashboardAccessConfig.editRule(${tmpKeyArg})'>Edit</button>
					<button class="edc-btn" onclick='_Pict.providers.DashboardAccessConfig.toggleRule(${tmpKeyArg})'>${pRule.Enabled === false ? 'Turn on' : 'Turn off'}</button>
					<button class="edc-btn is-danger" onclick='_Pict.providers.DashboardAccessConfig.removeRule(${tmpKeyArg})'>Remove</button>
				</div>
			</div>`;
		}).join('');
		const tmpList = tmpRows || '<div class="edc-note">No rules yet, so everyone sees every dashboard. Add a rule to start choosing who sees what — once a rule is on, people see only the dashboards a rule gives one of their roles.</div>';
		return `
			<div class="edc-field"><label class="edc-field-label">Customer</label>${tmpCustomer}</div>
			${tmpState.Message ? `<div class="edc-msg">${this._esc(tmpState.Message)}</div>` : ''}
			${tmpState.Error ? `<div class="edc-error">${this._esc(tmpState.Error)}</div>` : ''}
			<p class="edc-help">Each rule gives a group of roles some dashboards. A person sees every dashboard any of their roles is given. Roles given on a single project count only for dashboards that show one project, and only on that project.</p>
			<div class="edc-rules">${tmpList}</div>
			${tmpState.Draft ? this.renderEditorHTML() : '<button class="edc-btn" onclick="_Pict.providers.DashboardAccessConfig.newRule()"><i class="fas fa-plus"></i> Add a rule</button>'}
			<div class="edc-savebar"><button class="edc-save" ${(!tmpState.Dirty || tmpState.Saving) ? 'disabled' : ''} onclick="_Pict.providers.DashboardAccessConfig.save()">${tmpState.Saving ? 'Saving…' : 'Save changes'}</button>${tmpState.Dirty ? '<span class="edc-unsaved">Unsaved changes</span>' : ''}</div>
			${this.renderPreviewHTML()}`;
	}

	/** @return {string} */
	renderEditorHTML()
	{
		const tmpState = this.state;
		const tmpDraft = tmpState.Draft;
		const tmpDashboards = tmpState.Dashboards.length
			? tmpState.Dashboards.map((pDashboard) => this.renderDraftDashboardHTML(pDashboard)).join('')
			: '<span class="edc-hint">This customer has no dashboards yet.</span>';
		return `<div class="edc-editor">
			<div class="edc-field"><label class="edc-field-label">Rule name</label><input class="edc-input" value="${this._esc(tmpDraft.Label)}" placeholder="Lab technicians" onchange="_Pict.providers.DashboardAccessConfig.setDraft('Label', this.value)"></div>
			<div class="edc-field"><label class="edc-field-label">Who</label>${this.renderRolePickerHTML('Draft', 'Choose one or more roles')}</div>
			<div class="edc-field"><label class="edc-field-label">Sees these dashboards</label>
				<p class="edc-hint">Tick what these roles should see. Mark one to open first — it becomes their home page in LIMS. “Limit the data” narrows what a dashboard shows them, for example only their own lab’s samples.</p>
				<div class="dac-dashboards">${tmpDashboards}</div>
			</div>
			<div class="edc-editor-actions"><button class="edc-save" onclick="_Pict.providers.DashboardAccessConfig.applyDraft()">Keep this rule</button> <button class="edc-btn" onclick="_Pict.providers.DashboardAccessConfig.cancelEdit()">Cancel</button></div>
		</div>`;
	}

	/**
	 * @param {{ Hash: string, Title: string, Kind: string, ProjectBound: boolean }} pDashboard
	 * @return {string}
	 */
	renderDraftDashboardHTML(pDashboard)
	{
		const tmpDraft = this.state.Draft;
		const tmpHashArg = this._arg(pDashboard.Hash);
		const tmpChecked = tmpDraft.Dashboards.includes(pDashboard.Hash);
		const tmpNotes = [ pDashboard.ProjectBound ? 'one project at a time' : '', pDashboard.Kind === 'Legacy' ? 'classic dashboard' : '' ].filter(Boolean).join(', ');
		let tmpHTML = `<div class="dac-dashboard ${tmpChecked ? 'is-on' : ''}">
			<label class="edc-chip"><input type="checkbox" ${tmpChecked ? 'checked' : ''} onchange='_Pict.providers.DashboardAccessConfig.toggleDraftDashboard(${tmpHashArg})'> <b>${this._esc(pDashboard.Title)}</b>${tmpNotes ? ` <span class="edc-hint">(${this._esc(tmpNotes)})</span>` : ''}</label>`;
		if (tmpChecked)
		{
			tmpHTML += ` <label class="edc-chip dac-landing"><input type="radio" name="dac-landing" ${tmpDraft.Landing === pDashboard.Hash ? 'checked' : ''} onclick='_Pict.providers.DashboardAccessConfig.setDraftLanding(${tmpHashArg})'> Open first</label>`;
			if (pDashboard.Kind !== 'Legacy')
			{
				const tmpClauses = tmpDraft.DataFilters[pDashboard.Hash] || [];
				tmpHTML += `<div class="dac-filters">${tmpClauses.map((pClause, pIndex) => this.renderDraftFilterHTML(pDashboard.Hash, pClause, pIndex)).join('')}
					<button class="edc-btn" onclick='_Pict.providers.DashboardAccessConfig.addDraftFilter(${tmpHashArg})'><i class="fas fa-filter"></i> ${tmpClauses.length ? 'Add another limit' : 'Limit the data'}</button></div>`;
			}
		}
		return `${tmpHTML}</div>`;
	}

	/**
	 * One "only rows where <field> <is> <value>" line.
	 *
	 * @param {string} pHash
	 * @param {FilterClause} pClause
	 * @param {number} pIndex
	 * @return {string}
	 */
	renderDraftFilterHTML(pHash, pClause, pIndex)
	{
		const tmpCall = (pPart) => `_Pict.providers.DashboardAccessConfig.setDraftFilter(${this._arg(pHash)}, ${pIndex}, '${pPart}', this.value)`;
		const tmpFields = this.state.Fields[pHash] || [];
		const tmpFieldInput = tmpFields.length
			? `<select class="edc-select edc-narrow" onchange="${this._esc(tmpCall('Field'))}"><option value="">Choose a field</option>${tmpFields.map((pField) => `<option ${pClause.Field === pField ? 'selected' : ''}>${this._esc(pField)}</option>`).join('')}</select>`
			: `<input class="edc-input dac-field" value="${this._esc(pClause.Field)}" placeholder="Field, e.g. IDLab" onchange="${this._esc(tmpCall('Field'))}">`;
		const tmpOperators = Object.entries(libDashboardAccess.OPERATORS).map(([ pKey, pOperator ]) => `<option value="${pKey}" ${pClause.Operator === pKey ? 'selected' : ''}>${this._esc(pOperator.Label)}</option>`).join('');
		const tmpTokenMatch = /^\{~D:([A-Za-z0-9_.]+)~\}$/.exec(String(pClause.Value || ''));
		const tmpSource = tmpTokenMatch ? tmpTokenMatch[1] : TYPED_VALUE;
		const tmpSources = [ `<option value="${TYPED_VALUE}" ${tmpSource === TYPED_VALUE ? 'selected' : ''}>a value I type</option>` ]
			.concat(Object.entries(libDashboardAccess.TOKENS).map(([ pToken, pDefinition ]) => `<option value="${pToken}" ${tmpSource === pToken ? 'selected' : ''}>${this._esc(pDefinition.Label)}</option>`)).join('');
		const tmpTyped = (tmpSource === TYPED_VALUE)
			? `<input class="edc-input dac-value" value="${this._esc(Array.isArray(pClause.Value) ? pClause.Value.join(', ') : pClause.Value)}" placeholder="${libDashboardAccess.OPERATORS[pClause.Operator] && libDashboardAccess.OPERATORS[pClause.Operator].List ? 'Values, separated by commas' : 'Value'}" onchange="${this._esc(tmpCall('Value'))}">`
			: '';
		return `<div class="dac-filter"><span class="edc-hint">Only rows where</span> ${tmpFieldInput}
			<select class="edc-select edc-narrow" onchange="${this._esc(tmpCall('Operator'))}">${tmpOperators}</select>
			<select class="edc-select edc-narrow" onchange="${this._esc(tmpCall('ValueSource'))}">${tmpSources}</select> ${tmpTyped}
			<button type="button" class="edc-tag-x" title="Remove this limit" onclick='_Pict.providers.DashboardAccessConfig.removeDraftFilter(${this._arg(pHash)}, ${pIndex})'>×</button></div>`;
	}

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @param {string} pNoneLabel
	 * @return {string}
	 */
	renderRolePickerHTML(pContext, pNoneLabel)
	{
		const tmpState = this.state;
		if (!tmpState.Roles.length)
		{
			return '<span class="edc-hint">No roles found for this customer.</span>';
		}
		const tmpTags = this._chosenRoles(pContext).map((pHash) => `<span class="edc-tag">${this._esc(this.roleName(pHash))}<button type="button" class="edc-tag-x" title="Remove" onclick='_Pict.providers.DashboardAccessConfig.dropRole("${pContext}", ${this._arg(pHash)})'>×</button></span>`).join('');
		return `<div class="edc-picker">
			<div class="edc-tags">${tmpTags || `<span class="edc-hint">${this._esc(pNoneLabel)}</span>`}</div>
			<input id="DAC-RoleSearch-${pContext}" class="edc-input" autocomplete="off" placeholder="Find a role to add (${tmpState.Roles.length} roles)" value="${this._esc(tmpState.RoleSearch[pContext])}" oninput="_Pict.providers.DashboardAccessConfig.searchRoles('${pContext}', this.value)" onfocus="_Pict.providers.DashboardAccessConfig.searchRoles('${pContext}', this.value)">
			<div id="DAC-RoleSuggest-${pContext}" class="edc-suggest">${tmpState.RoleSearch[pContext] ? this.renderRoleSuggestionsHTML(pContext) : ''}</div>
		</div>`;
	}

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @return {string}
	 */
	renderRoleSuggestionsHTML(pContext)
	{
		const tmpFound = this.findRoles(pContext, this.state.RoleSearch[pContext]);
		if (!tmpFound.Matches.length)
		{
			return '<span class="edc-hint">No roles match.</span>';
		}
		const tmpButtons = tmpFound.Matches.map((pRole) => `<button type="button" class="edc-suggestion" onclick='_Pict.providers.DashboardAccessConfig.addRole("${pContext}", ${this._arg(pRole.Hash)})'>${this._esc(pRole.Name)}${pRole.PermissionLevel ? ` <span class="edc-hint">${this._esc(pRole.PermissionLevel)}</span>` : ''}</button>`).join('');
		return `${tmpButtons}${tmpFound.More ? `<span class="edc-hint">and ${tmpFound.More} more; keep typing to narrow it down.</span>` : ''}`;
	}

	/** @return {string} */
	renderPreviewHTML()
	{
		const tmpState = this.state;
		let tmpResultHTML = '';
		try
		{
			const tmpResult = this.preview();
			if (!tmpResult.Enforced)
			{
				tmpResultHTML = '<div>No rules are on, so <b>everyone sees every dashboard</b>.</div>';
			}
			else if (!tmpState.PreviewRoles.length)
			{
				tmpResultHTML = '<div>Choose some roles above to see what a person with them gets.</div>';
			}
			else if (!tmpResult.Dashboards.length)
			{
				tmpResultHTML = '<div><b>No dashboards.</b> None of these roles is in a rule that is turned on.</div>';
			}
			else
			{
				const tmpItems = tmpResult.Dashboards.map((pDashboard) =>
				{
					const tmpLimits = (pDashboard.DataFilter || []).concat(pDashboard.Unresolved || []);
					return `<li><b>${this._esc(pDashboard.Title)}</b>${tmpLimits.length ? ` — only rows where ${this._esc(this.describeFilters(tmpLimits))}` : ' — everything they can already reach'}</li>`;
				}).join('');
				tmpResultHTML = `<div><b>Sees:</b><ul class="dac-preview-list">${tmpItems}</ul></div>` +
					`<div><b>Opens first:</b> ${tmpResult.Landing ? this._esc(this.dashboard(tmpResult.Landing).Title) : 'the list of their dashboards'}</div>`;
			}
			tmpResultHTML += `<details class="edc-explain"><summary>Why</summary><ul>${tmpResult.Explain.map((pLine) => `<li>${this._esc(pLine)}</li>`).join('')}</ul>
				<p>When two rules give the same dashboard with different limits, the person gets the wider view: only the limits both rules share apply.</p></details>`;
		}
		catch (pError)
		{
			tmpResultHTML = `<div class="edc-error">Preview unavailable: ${this._esc((pError && pError.message) || pError)}</div>`;
		}
		return `<div class="edc-preview">
			<div class="edc-field-label">Try it: who sees what</div>
			<div class="edc-field"><label class="edc-field-label">A person with these roles</label>${this.renderRolePickerHTML('Preview', 'No roles')}</div>
			<div class="edc-preview-result">${tmpResultHTML}</div>
			<p class="edc-hint">Data limits narrow what a dashboard shows; they don’t widen anyone’s access. People never see records they couldn’t already open.</p>
		</div>`;
	}
}

module.exports = DashboardAccessConfigProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'DashboardAccessConfig',
	AutoInitialize: true,
	AutoInitializeOrdinal: 233,
};
